import { useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Loader2, CheckCircle, KeyRound } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminLayout } from './AdminLayout';

export function AdminSettings() {
  const { authHeaders, API, user } = useAdminAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (form.next !== form.confirm) {
      setError('Neue Passwörter stimmen nicht überein');
      return;
    }
    if (form.next.length < 8) {
      setError('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/me/password`,
        { current_password: form.current, new_password: form.next },
        { headers: authHeaders() }
      );
      setSuccess(true);
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8 max-w-xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Einstellungen</h1>
          <p className="text-sm text-slate-500 mt-0.5">Konto &amp; Sicherheit</p>
        </div>

        {/* Account info */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-300 mb-3">Konto-Informationen</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Name</span>
              <span className="text-white font-medium">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">E-Mail</span>
              <span className="text-white">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Rolle</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user?.role === 'admin' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-slate-700 text-slate-400'
              }`}>
                {user?.role === 'admin' ? 'Administrator' : 'Editor'}
              </span>
            </div>
          </div>
        </div>

        {/* Password change */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-cyan-400" />
            <h2 className="text-sm font-semibold text-slate-300">Passwort ändern</h2>
          </div>

          {success && (
            <div data-testid="password-change-success"
              className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm text-green-400 mb-4">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              Passwort erfolgreich geändert
            </div>
          )}

          {error && (
            <div data-testid="password-change-error"
              className="px-4 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            {[
              { key: 'current', label: 'Aktuelles Passwort', testId: 'current-password' },
              { key: 'next',    label: 'Neues Passwort',     testId: 'new-password-field' },
              { key: 'confirm', label: 'Passwort bestätigen', testId: 'confirm-password-field' },
            ].map(({ key, label, testId }) => (
              <div key={key} className="space-y-1">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form[key]}
                    onChange={e => set(key, e.target.value)}
                    required
                    data-testid={testId}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  />
                  {key === 'current' && (
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button
              type="submit"
              disabled={loading}
              data-testid="change-password-submit"
              className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Passwort speichern
            </button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
